import { useCurrencySelection } from "@/redux/profile/hooks/UseCurrencySelection";
import {
  StatisticsContext,
  StatisticsContextType,
} from "@/features/statistics/StatisticsContext";
import { createResultTableConfig } from "@/features/statistics/config/ResultTableConfig";
import { useStatisticsYearSelect } from "@/features/statistics/hooks/UseStatisticsYearSelector";
import { ResultTable } from "@/features/statistics/components/ResultTable";
import { SalesRegionCodes } from "@/features/statistics/types/SalesRegionsCodesEnum";
import { Card, CardHeader } from "@stenajs-webui/elements";
import * as React from "react";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router";
import { routeFactory } from "../../../../RouteFactory";
import { getNegotiationStatusCodes } from "../../lib/transformers/NegotiationStatusFormModelTransformer";
import { useSalesRegionListQuery } from "../../hooks/UseSalesRegionListQuery";
import { useSalesRegionListVolumeAndTargetQuery } from "../../hooks/UseSalesRegionListVolumeAndTargetQuery";
import { transformResultToTableRow } from "../../lib/transformers/sales-region.transformer";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { Helmet } from "react-helmet";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { Box } from "@stenajs-webui/core";

export const StatisticsBySalesRegionListScreen: React.FC = () => {
  const navigate = useNavigate();
  const { year: negotiationYear } = useStatisticsYearSelect();
  const { currency: currencyCode } = useCurrencySelection();
  const {
    salesRegionCustomerOrVolumeFilter,
    setSalesRegionCustomerOrVolumeFilter,
    salesRegionSelectedStatuses,
    setSalesRegionSelectedStatuses,
  } = useContext(StatisticsContext) as StatisticsContextType;

  const { loading, salesRegionItems } = useSalesRegionListQuery({
    year: negotiationYear,
    salesRegionCodes: Object.values(SalesRegionCodes),
  });

  const negotiationStatusCodes = useMemo(
    () => getNegotiationStatusCodes(salesRegionSelectedStatuses),
    [salesRegionSelectedStatuses]
  );

  const { loading: loadingVolumeAndTarget, volumeAndTarget } =
    useSalesRegionListVolumeAndTargetQuery({
      year: negotiationYear,
      salesRegionCodes: Object.values(SalesRegionCodes),
      negotiationStatusCodes,
      currencyCode,
    });

  const resultRows = useMemo(
    () =>
      salesRegionItems?.map((salesRegionItem) =>
        transformResultToTableRow(
          salesRegionItem,
          loadingVolumeAndTarget ? [] : volumeAndTarget ?? [],
          salesRegionCustomerOrVolumeFilter
        )
      ),
    [
      salesRegionItems,
      volumeAndTarget,
      loadingVolumeAndTarget,
      salesRegionCustomerOrVolumeFilter,
    ]
  );

  const resultConfig = createResultTableConfig(
    "Region",
    (id) => navigate(routeFactory.statistics.salesRegionDetails(id)),
    currencyCode,
    salesRegionSelectedStatuses,
    setSalesRegionSelectedStatuses,
    salesRegionCustomerOrVolumeFilter,
    setSalesRegionCustomerOrVolumeFilter
  );

  return (
    <>
      <Helmet title="Sales region - Statistics - FPP" />
      <PageLayout>
        <Card minWidth={"900px"}>
          <CardHeader text={"Sales Region"} />
          <Box data-testid={testIdConstants.statisticsSalesRegionResultTable}>
            <ResultTable
              items={resultRows}
              config={resultConfig}
              loading={loading || loadingVolumeAndTarget}
            />
          </Box>
        </Card>
      </PageLayout>
    </>
  );
};
